import { Button } from '@/components/ui/shadcn/button';
import { cn } from '@/lib/utils';
import { Image as ImageIcon, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';

interface PhotoUploadProps {
    photos: File[];
    onPhotosChange: (photos: File[]) => void;
    maxPhotos?: number;
    maxSizeMB?: number;
}

export function PhotoUpload({ photos, onPhotosChange, maxPhotos = 10, maxSizeMB = 5 }: PhotoUploadProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);

    const handleFileSelect = (files: FileList | null) => {
        if (!files) return;

        const newFiles = Array.from(files).filter((file) => {
            if (!file.type.startsWith('image/')) {
                alert('Please select only image files');
                return false;
            }
            if (file.size > maxSizeMB * 1024 * 1024) {
                alert(`File size must be less than ${maxSizeMB}MB`);
                return false;
            }
            return true;
        });

        if (photos.length + newFiles.length > maxPhotos) {
            alert(`Maximum ${maxPhotos} photos allowed`);
            return;
        }

        onPhotosChange([...photos, ...newFiles]);
    };

    const handleRemove = (index: number) => {
        onPhotosChange(photos.filter((_, i) => i !== index));
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        handleFileSelect(e.dataTransfer.files);
    };

    return (
        <div className="space-y-4">
            <div
                className={cn(
                    'rounded-lg border-2 border-dashed p-6 text-center transition-colors',
                    dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
                    photos.length >= maxPhotos && 'cursor-not-allowed opacity-50',
                )}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileSelect(e.target.files)}
                    className="hidden"
                    disabled={photos.length >= maxPhotos}
                />
                <ImageIcon className="text-muted-foreground mx-auto mb-3 h-10 w-10" />
                <p className="mb-1 text-sm font-medium">
                    {photos.length >= maxPhotos ? `Maximum ${maxPhotos} photos reached` : 'Drop photos here or click to upload'}
                </p>
                <p className="text-muted-foreground mb-3 text-xs">PNG, JPG, GIF up to {maxSizeMB}MB each</p>
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={photos.length >= maxPhotos}>
                    <Upload className="mr-2 h-4 w-4" />
                    Select Photos
                </Button>
            </div>

            {photos.length > 0 && (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                    {photos.map((photo, index) => (
                        <div key={index} className="group bg-background relative overflow-hidden rounded-lg border">
                            <div className="aspect-square">
                                <img src={URL.createObjectURL(photo)} alt={`Photo ${index + 1}`} className="h-full w-full object-cover" />
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRemove(index)}
                                className="bg-destructive text-destructive-foreground absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full opacity-0 transition-opacity group-hover:opacity-100"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
